import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAPI } from 'context/api-context';
import { useLocale } from 'context/locale-context';
import { useAuth } from 'context/auth-context';
import { useClinics } from 'context/clinics-context';

import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { DataTable, DataTableFilterMeta } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { confirmDialog } from 'primereact/confirmdialog';

import Layout from 'components/Layout/Layout';
import Overlay from 'components/Overlay';
import AssignQuestionnaire from './components/AssignQuestionnaire';
import AssignPartnerPatientKey from './components/AssignPartnerPatientKey';
import UserStatusBadge from './components/UserStatusBadge';
import ExportTable from './components/ExportTable';

import {
  User,
  ErrorResponse,
  ItemsResponse,
  DeleteResponse,
  ClinicProperties,
  Questionnaire,
} from 'shared/interfaces';
import makeDate from 'shared/helpers/makeDate';

type Response = ErrorResponse & ItemsResponse & DeleteResponse;
type QuestionnairesResponse = ErrorResponse & ItemsResponse & Questionnaire;

interface UsersProps {
  archived?: boolean;
}

enum HandleUserOptions {
  archive = 'archive',
  reactivate = 'reactivate',
  delete = 'delete',
}

type HandleUserType =
  | HandleUserOptions.archive
  | HandleUserOptions.reactivate
  | HandleUserOptions.delete;

const handleUserConfirmDialogText = {
  archive: {
    message: 'confirmArchiveUser',
    acceptLabel: 'archiveUser',
  },
  reactivate: {
    message: 'confirmReactivateUser',
    acceptLabel: 'reactivateUser',
  },
  delete: {
    message: 'confirmDeleteUser',
    acceptLabel: 'deleteUser',
  },
};

const dataTableOperatorFilterMetaData = {
  operator: FilterOperator.AND,
  constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
};

const Users = ({ archived }: UsersProps) => {
  const navigate = useNavigate();
  const { fetchAPI } = useAPI();
  const { getLocaleOption, locale } = useLocale();
  const { checkUserPrivileges, login: refreshUnreadMessages } = useAuth();
  const { clinics, assignedClinic } = useClinics();

  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);

  const [filters, setFilters] = useState<DataTableFilterMeta>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    username: dataTableOperatorFilterMetaData,
    'person.firstName': dataTableOperatorFilterMetaData,
    'person.lastName': dataTableOperatorFilterMetaData,
    userState: dataTableOperatorFilterMetaData,
    partnerPatientKey: dataTableOperatorFilterMetaData,
  });

  const [fetching, setFetching] = useState(false);

  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const isClinic = checkUserPrivileges('IS_CLINIC');
  const isFertilly = checkUserPrivileges('IS_FERTILLY');
  const accessAssign = checkUserPrivileges('ASSIGN_SAME_CLINIC_QUESTIONNAIRE');
  const accessArchive = checkUserPrivileges('ARCHIVE_USER');
  const accessReactivate = checkUserPrivileges('REACTIVATE_USER');
  const accessDelete = checkUserPrivileges('DELETE_USER');
  const accessClinics = checkUserPrivileges('VIS_MENU__ADMIN__CLINIC');

  useEffect(() => {
    const getUsers = async () => {
      setUsers([]);
      setFetching(true);

      const usersResponse: Response = await fetchAPI('/users/', {
        withToken: true,
      });

      if (usersResponse.error) {
        setError(true);
        return setErrorMessage(usersResponse.message);
      }

      let items: User[] = usersResponse.items || [];

      if (!accessClinics) {
        items = [...items].filter(
          (item: User) =>
            item.roles[0]?.name !== 'CLINIC' && item.roles[0]?.name !== 'MEDITEX_CLINIC'
        );
      }

      items = items.filter(
        archived ? (item: User) => item.archived : (item: User) => !item.archived
      );

      setUsers(items);
      setFilteredUsers(items);
      setFetching(false);
    };

    getUsers();
  }, [accessClinics, archived, fetchAPI]);

  useEffect(() => {
    const getQuestionnaires = async () => {
      const response: QuestionnairesResponse = await fetchAPI('/surveys/', {
        withToken: true,
      });

      const questionnaireItems: Questionnaire[] = response.items;

      if (response.error) {
        setError(true);
        return setErrorMessage(response.message);
      }

      setQuestionnaires(
        questionnaireItems
          .filter((item) => item.entityState !== 'DELETED')
          .sort((a, b) => {
            if (a.name < b.name) return -1;
            if (a.name > b.name) return 1;
            return 0;
          })
      );
    };

    getQuestionnaires();
  }, [fetchAPI]);

  const onGlobalFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    let _filters = { ...filters };

    // @ts-ignore:
    _filters['global'].value = value;

    setFilters(_filters);
  };

  const tableHeaderTemplate = () => {
    // @ts-ignore:
    const value = filters['global'] ? filters['global'].value : '';

    return (
      <div className='table-header flex align-items-center justify-content-between'>
        <h5 className='mx-0 my-1'>{getLocaleOption('users')}</h5>
        <div className='flex align-items-center'>
          <span className='p-input-icon-left mr-3'>
            <i className='pi pi-search' />
            <InputText
              value={value || ''}
              onChange={(e) => onGlobalFilterChange(e)}
              type='search'
              placeholder={`${getLocaleOption('search')}...`}
            />
          </span>
          <ExportTable users={filteredUsers} />
        </div>
      </div>
    );
  };

  const selectUserHandler = (user: User) => {
    navigate('/user', { state: { user, users } });
  };

  const userBodyTemplate = (data: User) => {
    return (
      <Button
        label={data.username}
        className='p-button-text'
        onClick={() => selectUserHandler(data)}
      />
    );
  };

  const messagesBodyTemplate = (data: User) => {
    const { totalUnreadMessages } = data;

    return (
      <Button
        label={
          totalUnreadMessages && totalUnreadMessages > 0
            ? totalUnreadMessages.toString()
            : '0'
        }
        className={`${
          totalUnreadMessages ? 'p-button-danger' : 'p-button-text'
        } text-xl font-bold`}
        onClick={() => selectUserHandler(data)}
      />
    );
  };

  const statusBodyTemplate = (data: User) => <UserStatusBadge status={data.userState} />;

  const dateBodyTemplate = (data: User) =>
    data.person?.birthday && makeDate(new Date(data.person.birthday), locale);

  const userCreatedOnDateBodyTemplate = (data: User) =>
    data.createdOn && makeDate(new Date(data.createdOn), locale);

  const latestActionBodyTemplate = (data: User) =>
    data?.lastActionOn && makeDate(new Date(data.lastActionOn), locale);

  const interfaceBodyTemplate = (user: User) => {
    const { partnerPatientKey } = user;

    return (
      <Overlay
        style={{ width: '400px' }}
        className={partnerPatientKey ? 'p-button-text' : ''}
        label={partnerPatientKey ? partnerPatientKey : getLocaleOption('assign')}
      >
        <AssignPartnerPatientKey user={user} users={users} setUsers={setUsers} />
      </Overlay>
    );
  };

  const assignBodyTemplate = (user: User) => {
    return (
      <Overlay style={{ width: '400px' }} label={getLocaleOption('assign')}>
        <AssignQuestionnaire
          user={user}
          questionnaires={questionnaires}
          error={error}
          errorMessage={errorMessage}
        />
      </Overlay>
    );
  };

  const archiveReactivateDeleteHandler = async (type: HandleUserType, id: number) => {
    const response: User & Response = await fetchAPI(`/users/${id}/${type}/`, {
      method: type === HandleUserOptions.delete ? 'DELETE' : 'POST',
      withToken: true,
    });

    if (response.message) {
      setError(true);
      setErrorMessage(response.message);
      return;
    }

    if (response.deleted) {
      const filter = users.filter((user) => user.id !== id);
      setUsers(filter);
      return;
    }

    const check =
      type === HandleUserOptions.archive ? response.archived : !response.archived;

    if (check) {
      const filter = users.filter((user) => user.id !== response.id);
      setUsers(filter);
    }

    refreshUnreadMessages();
  };

  const handleUserConfirmDialog = (
    type: HandleUserType,
    accept: (arg1: HandleUserType, arg2: number) => Promise<void>,
    id: number
  ) => {
    confirmDialog({
      header: getLocaleOption('confirmation'),
      message: getLocaleOption(handleUserConfirmDialogText[type].message),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: getLocaleOption(handleUserConfirmDialogText[type].acceptLabel),
      rejectLabel: getLocaleOption('cancel'),
      accept: () => accept(type, id),
      reject: () => false,
    });
  };

  const archiveUserBodyTemplate = (user: User) => {
    return (
      <Button
        label={getLocaleOption(HandleUserOptions.archive)}
        className='p-button-danger'
        onClick={() =>
          handleUserConfirmDialog(
            HandleUserOptions.archive,
            archiveReactivateDeleteHandler,
            user.id
          )
        }
      />
    );
  };

  const reactivateUserBodyTemplate = (user: User) => {
    return (
      <Button
        label={getLocaleOption(HandleUserOptions.reactivate)}
        className='p-button-success'
        onClick={() =>
          handleUserConfirmDialog(
            HandleUserOptions.reactivate,
            archiveReactivateDeleteHandler,
            user.id
          )
        }
      />
    );
  };

  const deleteUserBodyTemplate = (user: User) => {
    return (
      <Button
        label={getLocaleOption(HandleUserOptions.delete)}
        className='p-button-danger'
        onClick={() =>
          handleUserConfirmDialog(
            HandleUserOptions.delete,
            archiveReactivateDeleteHandler,
            user.id
          )
        }
      />
    );
  };

  const assignedClinicTemplate = (user: User) => (
    <p>{clinics.find((c) => c.id === user.clinicId)?.name || ''}</p>
  );

  return (
    <Layout>
      {error ? (
        <h5 className='mb-0'>{errorMessage}</h5>
      ) : (
        <DataTable
          loading={fetching}
          value={users}
          responsiveLayout='scroll'
          showGridlines
          header={tableHeaderTemplate}
          filters={filters}
          onFilter={(e) => setFilters(e.filters)}
          onValueChange={(users) => setFilteredUsers(users)}
          stateStorage='session'
          stateKey='user-table-state-local'
          paginator
          paginatorTemplate='CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown'
          currentPageReportTemplate={getLocaleOption('tableNumberingTemplate')}
          rows={10}
          rowsPerPageOptions={[10, 20, 50]}
          emptyMessage={getLocaleOption('tableNoAvailableOptions')}
        >
          <Column
            header={getLocaleOption('lastName')}
            body={(data: User) => data.person?.lastName}
            filter
            filterField='person.lastName'
            sortable
            sortField='person.lastName'
          />
          <Column
            header={getLocaleOption('firstName')}
            body={(data: User) => data.person?.firstName}
            filter
            filterField='person.firstName'
            sortable
            sortField='person.firstName'
          />
          <Column
            field='username'
            header={getLocaleOption('username')}
            body={userBodyTemplate}
            filter
            filterField='username'
          />
          <Column
            header={getLocaleOption('birthday')}
            body={dateBodyTemplate}
            sortable
            sortField='person.birthday'
          />
          {accessClinics && (
            <Column
              header={getLocaleOption('assignedUserRole')}
              body={(data: User) => data.roles[0]?.name}
            />
          )}
          {accessClinics && (
            <Column
              header={getLocaleOption('assignedClinics')}
              body={assignedClinicTemplate}
            />
          )}
          <Column
            header={getLocaleOption('invitationDate')}
            body={userCreatedOnDateBodyTemplate}
            sortable
            sortField='createdOn'
          />
          <Column
            header={getLocaleOption('latestAction')}
            body={latestActionBodyTemplate}
            sortable
            sortField='lastActionOn'
          />
          {isClinic && assignedClinic?.chatEnabled && (
            <Column
              align='center'
              header={getLocaleOption('messages')}
              body={messagesBodyTemplate}
              sortable
              sortField='totalUnreadMessages'
            />
          )}
          {(isClinic || isFertilly) && (
            <Column
              align='center'
              header={getLocaleOption('status')}
              body={statusBodyTemplate}
              sortable
              sortField='userState'
            />
          )}
          {isClinic && (assignedClinic?.clinicProperty === ClinicProperties.meditex || assignedClinic?.clinicProperty === ClinicProperties.gdt) && (
            <Column
              filterField='partnerPatientKey'
              className='interfaceColumn'
              header={'Patient ID'}
              body={interfaceBodyTemplate}
              sortable
              sortField='partnerPatientKey'
            />
          )}
          {!archived && accessAssign && (
            <Column
              header={getLocaleOption('assignQuestionnaire')}
              body={assignBodyTemplate}
            />
          )}
          {!archived && accessArchive && (
            <Column
              header={getLocaleOption('archiveUser')}
              body={archiveUserBodyTemplate}
            />
          )}
          {archived && accessReactivate && (
            <Column
              header={getLocaleOption('reactivateUser')}
              body={reactivateUserBodyTemplate}
            />
          )}
          {archived && accessDelete && (
            <Column
              header={getLocaleOption('deleteUser')}
              body={deleteUserBodyTemplate}
            />
          )}
        </DataTable>
      )}
    </Layout>
  );
};

export default Users;
